import React, { useState, useMemo, useCallback } from 'react';
import { Calculator, ArrowRight, AlertTriangle, CheckCircle, Users, Building, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import SEO from '../components/SEO';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
  Area
} from 'recharts';

// Salary data for different roles at different seniority levels (Annual base salary in USD)
const SALARY_DATA = {
  "Full-Stack": { junior: 12000, mid: 22000, senior: 38000 },
  "Backend": { junior: 13000, mid: 24000, senior: 42000 },
  "Frontend": { junior: 10000, mid: 18000, senior: 30000 },
  "Cloud/DevOps": { junior: 14000, mid: 26000, senior: 46000 },
  "Data Engineering": { junior: 13000, mid: 24000, senior: 44000 },
  "QA/Testing": { junior: 8000, mid: 14000, senior: 24000 },
  "Mobile": { junior: 12000, mid: 22000, senior: 38000 },
  "AI/ML": { junior: 16000, mid: 30000, senior: 55000 }
};

const ROLE_OPTIONS = [
  { value: "Full-Stack", label: "Full-Stack Engineering" },
  { value: "Backend", label: "Backend Engineering" },
  { value: "Frontend", label: "Frontend Engineering" },
  { value: "Cloud/DevOps", label: "Cloud / DevOps" },
  { value: "Data Engineering", label: "Data Engineering" },
  { value: "QA/Testing", label: "QA / Testing" },
  { value: "Mobile", label: "Mobile (iOS/Android)" },
  { value: "AI/ML", label: "AI / ML Engineering" }
];

const ENGAGEMENT_MODELS = [
  { value: "staffaug", name: "Staff Aug", description: "Engineers join your team directly" },
  { value: "gcc", name: "GCC", description: "Your own India entity & team" },
  { value: "managed", name: "Managed", description: "Fully managed delivery team" }
];

const CALENDLY_URL = "https://calendly.com/chengappa-nivadigital/30min";

// Get base URL from environment variable
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nivadigital.io';

// Helper functions
const formatCurrency = (value) => {
  return "$" + Math.round(value).toLocaleString("en-US");
};

const formatCurrencyK = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000000) return "$" + (value / 1000000).toFixed(1) + "M";
  if (abs >= 1000) return "$" + Math.round(value / 1000) + "K";
  return "$" + Math.round(value);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const GCCCalculator = () => {
  // State for calculator inputs
  const [teamSize, setTeamSize] = useState(10);
  const [seniorityMix, setSeniorityMix] = useState({ junior: 20, mid: 60, senior: 20 });
  const [engagementModel, setEngagementModel] = useState("gcc");
  const [selectedRoles, setSelectedRoles] = useState(["Full-Stack"]);
  const [usSalary, setUsSalary] = useState(160000);
  const [usRecruit, setUsRecruit] = useState(20000);

  // Handle seniority slider changes to ensure they sum to 100%
  const handleSeniorityChange = useCallback((type, newValue) => {
    setSeniorityMix(prev => {
      const updated = { ...prev, [type]: newValue };
      const total = updated.junior + updated.mid + updated.senior;
      
      if (total === 100) return updated;
      
      const diff = 100 - total;
      
      if (type === 'junior') {
        const ratio = (prev.mid + prev.senior) || 1;
        const newMid = Math.max(0, Math.round(prev.mid + diff * (prev.mid / ratio)));
        const newSenior = Math.max(0, 100 - newValue - newMid);
        return { junior: newValue, mid: newMid, senior: newSenior };
      } else if (type === 'mid') {
        const ratio = (prev.junior + prev.senior) || 1;
        const newJunior = Math.max(0, Math.round(prev.junior + diff * (prev.junior / ratio)));
        const newSenior = Math.max(0, 100 - newJunior - newValue);
        return { junior: newJunior, mid: newValue, senior: newSenior };
      } else {
        const ratio = (prev.junior + prev.mid) || 1;
        const newJunior = Math.max(0, Math.round(prev.junior + diff * (prev.junior / ratio)));
        const newMid = Math.max(0, 100 - newJunior - newValue);
        return { junior: newJunior, mid: newMid, senior: newValue };
      }
    });
  }, []);

  // Handle role selection
  const handleRoleChange = useCallback((role, checked) => {
    setSelectedRoles(prev => {
      if (checked) {
        return [...prev, role];
      } else {
        // Prevent unchecking all roles
        if (prev.length === 1 && prev[0] === role) {
          return prev;
        }
        return prev.filter(r => r !== role);
      }
    });
  }, []);

  // Calculate all costs and metrics
  const calculations = useMemo(() => {
    const n = teamSize;
    const { junior: j, mid: m, senior: s } = seniorityMix;
    const roles = selectedRoles.length > 0 ? selectedRoles : ["Full-Stack"];

    // Calculate average salaries across selected roles
    let juniorAvg = 0, midAvg = 0, seniorAvg = 0;
    roles.forEach(role => {
      const salaries = SALARY_DATA[role];
      juniorAvg += salaries.junior;
      midAvg += salaries.mid;
      seniorAvg += salaries.senior;
    });
    juniorAvg /= roles.length;
    midAvg /= roles.length;
    seniorAvg /= roles.length;

    // Weighted average base salary per engineer
    const weightedSalary = (j * juniorAvg + m * midAvg + s * seniorAvg) / 100;

    // India costs
    const indiaSalaries = weightedSalary * n;
    const indiaBenefits = indiaSalaries * 0.28; // 28% for benefits & statutory
    // HR & Recruiting cost - excluded for Staff Aug model
    const indiaRecruiting = engagementModel === "staffaug" ? 0 : weightedSalary * 0.10 * n;
    
    // Setup cost based on engagement model
    let setupCost = 0;
    if (engagementModel === "gcc") {
      setupCost = clamp(5000 * n, 40000, 120000);
    } else if (engagementModel === "managed") {
      setupCost = clamp(2500 * n, 15000, 60000);
    }
    
    const indiaManagement = indiaSalaries * 0.40; // 40% management/partner fee
    const indiaTotal = indiaSalaries + indiaBenefits + indiaRecruiting + setupCost + indiaManagement;
    // Ongoing cost - recruiting buffer excluded for Staff Aug
    const indiaOngoing = engagementModel === "staffaug" 
      ? indiaSalaries + indiaBenefits + indiaManagement 
      : indiaSalaries + indiaBenefits + indiaManagement + (weightedSalary * 0.10 * n * 0.25);

    // US costs
    const usSalaries = usSalary * n;
    const usBenefits = usSalaries * 0.25; // 25% for benefits & statutory
    const usRecruiting = usRecruit * n;
    const usTotal = usSalaries + usBenefits + usRecruiting;
    const usOngoing = usSalaries + usBenefits + (usRecruit * n * 0.20);

    // Savings and metrics
    const savings = usTotal - indiaTotal;
    const savingsPercent = (savings / usTotal) * 100;
    const usTCO = usTotal + usOngoing * 2;
    const indiaTCO = indiaTotal + indiaOngoing * 2;
    const tcoSavings = usTCO - indiaTCO;
    const monthlySavings = savings / 12;
    const costPerEngineer = indiaTotal / n;
    const paybackMonths = (setupCost > 0 && monthlySavings > 0) ? setupCost / monthlySavings : null;
    const hourlyRate = costPerEngineer / 2080; // 2080 working hours per year

    return {
      india: {
        salaries: indiaSalaries,
        benefits: indiaBenefits,
        recruiting: indiaRecruiting,
        setup: setupCost,
        management: indiaManagement,
        total: indiaTotal,
        ongoing: indiaOngoing
      },
      us: {
        salaries: usSalaries,
        benefits: usBenefits,
        recruiting: usRecruiting,
        total: usTotal,
        ongoing: usOngoing
      },
      savings,
      savingsPercent,
      tcoSavings,
      paybackMonths,
      hourlyRate,
      costPerEngineer
    };
  }, [teamSize, seniorityMix, engagementModel, selectedRoles, usSalary, usRecruit]);

  // Chart data for 3-year TCO
  const chartData = useMemo(() => {
    const { india, us } = calculations;
    let indiaCumulative = 0;
    let usCumulative = 0;

    return [
      {
        name: 'Year 1',
        india: india.total,
        us: us.total,
        cumulativeSavings: us.total - india.total
      },
      {
        name: 'Year 2',
        india: india.ongoing,
        us: us.ongoing,
        cumulativeSavings: (us.total + us.ongoing) - (india.total + india.ongoing)
      },
      {
        name: 'Year 3',
        india: india.ongoing,
        us: us.ongoing,
        cumulativeSavings: (us.total + us.ongoing * 2) - (india.total + india.ongoing * 2)
      }
    ];
  }, [calculations]);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-lg border border-gray-700">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Schema markup for SEO
  const structuredData = [
    // WebApplication Schema
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "India GCC Cost Calculator",
      "description": "Free interactive calculator for US companies to estimate the cost of building an engineering team in India via GCC setup, Staff Augmentation, or Managed IT Services. Shows India vs. US cost comparison, 3-year TCO, setup payback period, and effective hourly rate per engineer.",
      "url": `${BASE_URL}/gcc-cost-calculator`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "All",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "provider": {
        "@type": "Organization",
        "name": "Niva Digital",
        "url": BASE_URL
      },
      "featureList": [
        "India vs. US engineering cost comparison",
        "3-year total cost of ownership (TCO) analysis",
        "Setup payback period calculator",
        "Effective hourly rate per India engineer (2,080 hrs/yr)",
        "Seniority mix modeling: Junior, Mid-Level, Senior",
        "Three engagement models: Staff Augmentation, GCC, Managed"
      ]
    },
    // FAQPage Schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does it cost to hire a software engineer in India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A mid-level India engineer costs $23,000-$36,000 fully loaded per year (salary + 28% statutory). A US equivalent costs $120,000-$180,000. Stack matters: Cloud/DevOps and AI/ML command a 15-25% premium in both markets."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to set up an development center in India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Staff Augmentation: 3-4 weeks. Full GCC setup including entity registration, compliance, and initial hiring: 60-90 days with an experienced partner."
          }
        },
        {
          "@type": "Question",
          "name": "What is the difference between a GCC and outsourcing in India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A GCC is wholly owned by your company — engineers are your employees, IP is fully yours. Outsourcing delegates to a third-party vendor. GCCs deliver better IP control, retention, and long-term cost efficiency for teams of 10 or more."
          }
        },
        {
          "@type": "Question",
          "name": "Is India cheaper than Eastern Europe for software development?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. India rates run $18-$45/hr vs. $25-$70/hr in Eastern Europe (Poland, Romania, Ukraine) per 2025 benchmarks. Fully loaded annual costs are 30-40% lower for comparable India engineers."
          }
        },
        {
          "@type": "Question",
          "name": "Is it safe to hire engineers in India for a US tech company?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, with proper structure. India has strong IP laws and enforceable NDAs. The DPDP Act (2025) aligns with GDPR for data handling. Risk is operational, not legal — clear US-India escalation paths are the critical success factor."
          }
        },
        {
          "@type": "Question",
          "name": "What is the effective hourly rate for an India GCC engineer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "All-in effective rate (salary + benefits + recruiting + setup + management, over 2,080 hrs/yr) runs $18-$35/hr by seniority. A mid-level Full-Stack engineer in Bengaluru works out to $22-$28/hr."
          }
        },
        {
          "@type": "Question",
          "name": "What are the best cities in India to hire engineers for a GCC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bengaluru (880+ GCCs) leads for AI/ML and product engineering. Hyderabad is fastest-growing. Pune and Chennai offer 10-15% cost savings. Tier-2 cities like Coimbatore and Indore offer 20-30% savings."
          }
        },
        {
          "@type": "Question",
          "name": "What is the 3-year TCO for an India engineering team?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A 10-person GCC team: $1.2M-$1.8M over 3 years vs. $5.5M-$7M for a US equivalent — saving $3.5M-$5M. A 50-100 person GCC has 40-60% lower annual operating costs than the US equivalent."
          }
        }
      ]
    },
    // BreadcrumbList Schema
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": BASE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Tools",
          "item": `${BASE_URL}/tools`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "India GCC Cost Calculator",
          "item": `${BASE_URL}/gcc-cost-calculator`
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-red-50">
      <SEO
        title="India GCC Cost Calculator — Free Tool | NIVA Digital AI"
        description="See exactly what it costs to build an engineering team in India. Our free GCC cost calculator shows savings vs. US hiring — salaries, setup, and partner fees included."
        keywords="india gcc cost calculator, gcc cost calculator, how much does it cost to hire a software engineer in india, india vs us engineering cost, gcc setup india cost, gcc vs outsourcing india, development center india cost, india engineer hourly rate, staff augmentation india us companies, 3 year tco india team"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/gcc-cost-calculator`}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="inline-flex items-center space-x-2 bg-red-600/20 border border-red-600/30 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span>Free Tool</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            India GCC Cost Calculator: <span className="text-amber-400">See What Your Engineering Team Actually Costs</span>
          </h1>
          <h2 className="text-lg text-gray-400 max-w-2xl">
            Compare India vs. US Engineering Costs in 60 Seconds — Salaries, Benefits, Setup, and Partner Fees
          </h2>
          <p className="text-base text-gray-500 max-w-xl mt-4">
            Use this free India GCC cost calculator to model your exact team — by size, seniority, and stack. 
            Instantly compare GCC, Staff Augmentation, or Managed models to see your 3-year total cost of ownership and effective India hourly rate.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-[420px_1fr] gap-8">
            
            {/* Input Section */}
            <div className="space-y-6">
              
              {/* Team Configuration */}
              <Card className="border-2 hover:border-red-200 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Users className="text-red-600" size={18} />
                    <h3 className="text-xs font-bold tracking-wider text-red-600 uppercase">Your Team</h3>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* Team Size */}
                  <div className="mb-6">
                    <div className="flex justify-between items-baseline mb-3">
                      <span className="text-sm font-medium text-gray-700">Number of engineers</span>
                      <span className="bg-red-100 text-red-600 px-3 py-1 rounded text-sm font-mono font-medium">
                        {teamSize}
                      </span>
                    </div>
                    <Slider
                      value={[teamSize]}
                      onValueChange={([value]) => setTeamSize(value)}
                      min={2}
                      max={50}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Seniority Mix */}
                  <div className="mb-6">
                    <h4 className="text-xs font-bold tracking-wider text-gray-500 uppercase mb-4">Seniority Mix</h4>
                    
                    {/* Visual Bar */}
                    <div className="flex gap-0.5 h-2 rounded overflow-hidden mb-4">
                      <div className="bg-teal-500 transition-all duration-300" style={{ flex: seniorityMix.junior || 0.5 }}></div>
                      <div className="bg-blue-900 transition-all duration-300" style={{ flex: seniorityMix.mid || 0.5 }}></div>
                      <div className="bg-red-600 transition-all duration-300" style={{ flex: seniorityMix.senior || 0.5 }}></div>
                    </div>

                    {/* Labels */}
                    <div className="flex justify-between text-xs font-medium mb-4">
                      <span className="text-teal-600">Junior <strong>{seniorityMix.junior}%</strong></span>
                      <span className="text-blue-900">Mid-Level <strong>{seniorityMix.mid}%</strong></span>
                      <span className="text-red-600">Senior <strong>{seniorityMix.senior}%</strong></span>
                    </div>

                    {/* Junior Slider */}
                    <div className="mb-3">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-sm font-medium text-teal-600">Junior</span>
                        <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded text-xs font-mono">
                          {seniorityMix.junior}%
                        </span>
                      </div>
                      <Slider
                        value={[seniorityMix.junior]}
                        onValueChange={([value]) => handleSeniorityChange('junior', value)}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>

                    {/* Mid-Level Slider */}
                    <div className="mb-3">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-sm font-medium text-blue-900">Mid-Level</span>
                        <span className="bg-blue-100 text-blue-900 px-2 py-0.5 rounded text-xs font-mono">
                          {seniorityMix.mid}%
                        </span>
                      </div>
                      <Slider
                        value={[seniorityMix.mid]}
                        onValueChange={([value]) => handleSeniorityChange('mid', value)}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>

                    {/* Senior Slider */}
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-sm font-medium text-red-600">Senior</span>
                        <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded text-xs font-mono">
                          {seniorityMix.senior}%
                        </span>
                      </div>
                      <Slider
                        value={[seniorityMix.senior]}
                        onValueChange={([value]) => handleSeniorityChange('senior', value)}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Engagement Model */}
                  <div>
                    <h4 className="text-xs font-bold tracking-wider text-gray-500 uppercase mb-4">Engagement Model</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {ENGAGEMENT_MODELS.map((model) => (
                        <button
                          key={model.value}
                          onClick={() => setEngagementModel(model.value)}
                          className={`p-3 rounded-lg border-2 text-center transition-all duration-200 ${
                            engagementModel === model.value
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : 'border-gray-200 bg-white hover:border-gray-400'
                          }`}
                        >
                          <span className="block text-xs font-semibold mb-1">{model.name}</span>
                          <span className={`block text-[10px] leading-tight ${
                            engagementModel === model.value ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {model.description}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Role Type */}
              <Card className="border-2 hover:border-red-200 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="text-red-600" size={18} />
                    <h3 className="text-xs font-bold tracking-wider text-red-600 uppercase">Role Type</h3>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Select all role types needed <span className="text-gray-400 text-xs">— costs averaged across selection</span>
                  </p>
                  <div className="space-y-2">
                    {ROLE_OPTIONS.map((role) => (
                      <label
                        key={role.value}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          selectedRoles.includes(role.value)
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-200 bg-white hover:border-gray-400'
                        }`}
                      >
                        <Checkbox
                          checked={selectedRoles.includes(role.value)}
                          onCheckedChange={(checked) => handleRoleChange(role.value, checked)}
                          className="border-current"
                        />
                        <span className="text-sm font-medium">{role.label}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* US Comparison Baseline */}
              <Card className="border-2 hover:border-red-200 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Building className="text-red-600" size={18} />
                    <h3 className="text-xs font-bold tracking-wider text-red-600 uppercase">US Comparison Baseline</h3>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* US Salary */}
                  <div className="mb-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700 block">Avg US fully-loaded salary</span>
                        <span className="text-xs text-gray-500">Per engineer, incl. benefits, taxes, PTO</span>
                      </div>
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm font-mono font-medium">
                        ${Math.round(usSalary / 1000)}K
                      </span>
                    </div>
                    <Slider
                      value={[usSalary]}
                      onValueChange={([value]) => setUsSalary(value)}
                      min={80000}
                      max={250000}
                      step={5000}
                      className="w-full"
                    />
                  </div>

                  {/* US Recruiting Cost */}
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700 block">US recruiting cost per hire</span>
                        <span className="text-xs text-gray-500">Agency fees, time-to-hire, onboarding</span>
                      </div>
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm font-mono font-medium">
                        ${Math.round(usRecruit / 1000)}K
                      </span>
                    </div>
                    <Slider
                      value={[usRecruit]}
                      onValueChange={([value]) => setUsRecruit(value)}
                      min={10000}
                      max={40000}
                      step={1000}
                      className="w-full"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Section */}
            <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
              
              {/* Main Savings Card */}
              <Card className="bg-gray-900 border-0 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-red-600/20 rounded-full blur-3xl"></div>
                <CardContent className="pt-8 pb-8 relative z-10">
                  <p className="text-xs font-semibold tracking-wider text-gray-400 uppercase mb-2">
                    Year 1 Savings vs. US Hiring
                  </p>
                  <p className="text-4xl md:text-5xl font-bold text-red-500 mb-1">
                    {formatCurrency(calculations.savings)}
                  </p>
                  <p className="text-lg font-mono text-amber-400 mb-3">
                    {calculations.savingsPercent.toFixed(1)}% less
                  </p>
                  <p className="text-sm text-gray-500">
                    less than hiring the equivalent team in the United States
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-700">
                    <div>
                      <p className="text-xs font-medium text-teal-400 uppercase tracking-wider mb-1">India Year 1</p>
                      <p className="text-xl font-mono text-teal-300">{formatCurrency(calculations.india.total)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">US Equivalent</p>
                      <p className="text-xl font-mono text-white">{formatCurrency(calculations.us.total)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-2">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">3-Year TCO Savings</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrencyK(calculations.tcoSavings)}</p>
                    <p className="text-xs text-gray-500 mt-1">cumulative vs. US</p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Setup Payback</p>
                    <p className="text-2xl font-bold text-teal-600">
                      {calculations.paybackMonths === null ? "N/A" : 
                       calculations.paybackMonths < 1 ? "< 1 mo" : 
                       `${calculations.paybackMonths.toFixed(1)} mo`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">months to recoup setup cost</p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Effective India Rate</p>
                    <p className="text-2xl font-bold text-amber-500">
                      ${Math.round(calculations.hourlyRate)}<span className="text-sm font-normal">/hr</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">per engineer per hour (2,080 hrs/yr)</p>
                  </CardContent>
                </Card>
                <Card className="border-2">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">India Cost Per Eng.</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrencyK(calculations.costPerEngineer)}</p>
                    <p className="text-xs text-gray-500 mt-1">annually, fully loaded</p>
                  </CardContent>
                </Card>
              </div>

              {/* Cost Breakdown Table */}
              <Card className="border-2 overflow-hidden">
                <div className="bg-gray-50 px-5 py-3 border-b">
                  <h3 className="text-xs font-bold tracking-wider text-gray-500 uppercase">Year 1 Cost Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Component</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-teal-600 uppercase">India</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-red-600 uppercase">US</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700">Salaries</td>
                        <td className="px-5 py-3 text-right font-mono text-teal-600">{formatCurrency(calculations.india.salaries)}</td>
                        <td className="px-5 py-3 text-right font-mono text-red-600">{formatCurrency(calculations.us.salaries)}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700">Benefits & Statutory</td>
                        <td className="px-5 py-3 text-right font-mono text-teal-600">{formatCurrency(calculations.india.benefits)}</td>
                        <td className="px-5 py-3 text-right font-mono text-red-600">{formatCurrency(calculations.us.benefits)}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700">HR & Recruiting</td>
                        <td className="px-5 py-3 text-right font-mono text-teal-600">{formatCurrency(calculations.india.recruiting)}</td>
                        <td className="px-5 py-3 text-right font-mono text-red-600">{formatCurrency(calculations.us.recruiting)}</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700">
                          Setup / Entity 
                          <span className="text-xs text-gray-400 ml-1">(Approx. and will be charged on actuals)</span>
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-teal-600">
                          {calculations.india.setup > 0 ? formatCurrency(calculations.india.setup) : "—"}
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-gray-400">—</td>
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-700">Management / Partner Fee</td>
                        <td className="px-5 py-3 text-right font-mono text-teal-600">{formatCurrency(calculations.india.management)}</td>
                        <td className="px-5 py-3 text-right font-mono text-gray-400">—</td>
                      </tr>
                      <tr className="bg-gray-50 border-t-2">
                        <td className="px-5 py-3 font-bold text-gray-900 uppercase text-xs tracking-wider">Total Year 1</td>
                        <td className="px-5 py-3 text-right font-mono font-bold text-teal-600">{formatCurrency(calculations.india.total)}</td>
                        <td className="px-5 py-3 text-right font-mono font-bold text-red-600">{formatCurrency(calculations.us.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>

          {/* Chart Section */}
          <Card className="mt-8 border-2 overflow-hidden">
            <CardContent className="pt-6 pb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">3-Year Cost of Ownership</h3>
                  <p className="text-sm text-gray-500">India vs. US — annual and cumulative savings</p>
                </div>
                <div className="mt-4 md:mt-0 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg px-5 py-3">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Total 3-Year Savings</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrencyK(calculations.tcoSavings)}</p>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#DC2626" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#DC2626" stopOpacity={0.05}/>
                      </linearGradient>
                      <linearGradient id="indiaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0E7C7B" stopOpacity={0.9}/>
                        <stop offset="100%" stopColor="#0E7C7B" stopOpacity={0.6}/>
                      </linearGradient>
                      <linearGradient id="usGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1B3A6B" stopOpacity={0.7}/>
                        <stop offset="100%" stopColor="#1B3A6B" stopOpacity={0.4}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 500 }} 
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickLine={false}
                    />
                    <YAxis 
                      yAxisId="left" 
                      tick={{ fill: '#6B7280', fontSize: 11 }} 
                      tickFormatter={(value) => formatCurrencyK(value)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      tick={{ fill: '#DC2626', fontSize: 11, fontWeight: 600 }} 
                      tickFormatter={(value) => formatCurrencyK(value)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span className="text-sm font-medium text-gray-600">{value}</span>}
                    />
                    <Bar 
                      yAxisId="left" 
                      dataKey="india" 
                      name="India Cost" 
                      fill="url(#indiaGradient)" 
                      stroke="#0E7C7B" 
                      strokeWidth={2}
                      radius={[6, 6, 0, 0]}
                      barSize={50}
                    />
                    <Bar 
                      yAxisId="left" 
                      dataKey="us" 
                      name="US Cost" 
                      fill="url(#usGradient)" 
                      stroke="#1B3A6B" 
                      strokeWidth={2}
                      radius={[6, 6, 0, 0]}
                      barSize={50}
                    />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="cumulativeSavings" 
                      name="Cumulative Savings" 
                      stroke="#DC2626" 
                      strokeWidth={3}
                      dot={{ fill: '#DC2626', r: 6, strokeWidth: 3, stroke: '#fff' }}
                      activeDot={{ r: 8, stroke: '#DC2626', strokeWidth: 2, fill: '#fff' }}
                      fill="url(#savingsGradient)"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gradient-to-b from-teal-500 to-teal-600"></div>
                    <span className="text-gray-600">Lower India costs = More savings</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-1 rounded bg-red-500"></div>
                    <span className="text-gray-600">Savings grow each year</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hidden Costs Advisory */}
          <Card className="mt-8 border-2 border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="text-amber-500" size={20} />
                <h3 className="font-bold text-gray-900">Budget These Separately</h3>
              </div>
              <p className="text-sm text-gray-500 italic mb-4">
                Note: The above calculated costs are approximate and the numbers can vary based on your specific requirements.
              </p>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <span className="text-amber-500 font-bold">→</span>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Attrition buffer:</strong> India IT has 15–25% annual attrition — plan for 1–2 re-hires per year at ~10% of salary each.
                  </p>
                </div>
                <div className="flex gap-3">
                  <span className="text-amber-500 font-bold">→</span>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">US management overhead:</strong> Expect 10–15% more coordination time from your US leads in the first 6 months.
                  </p>
                </div>
                <div className="flex gap-3">
                  <span className="text-amber-500 font-bold">→</span>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Notice periods:</strong> Senior Indian engineers typically serve 60–90 day notice — build this into your ramp timeline.
                  </p>
                </div>
                <div className="flex gap-3">
                  <span className="text-amber-500 font-bold">→</span>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Home office stipend:</strong> Budget ~$1,200 per person for equipment and connectivity setup.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <Card className="mt-8 bg-gray-900 border-0 overflow-hidden relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-64 bg-red-600/15 rounded-full blur-3xl"></div>
            <CardContent className="py-12 text-center relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to build your India team?
              </h2>
              <p className="text-gray-400 max-w-lg mx-auto mb-6">
                Book a free 30-minute strategy call. We'll map out your exact roadmap, team structure, and cost model.
              </p>
              <a 
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  className="bg-amber-400 hover:bg-amber-500 text-gray-900 font-bold px-8 py-6 text-lg"
                >
                  Book My Free Strategy Call <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
              <p className="text-xs text-gray-500 mt-4">
                No sales pitch. No obligation. Your roadmap is yours to keep.
              </p>
            </CardContent>
          </Card>

          {/* SEO Content: What Goes Into Your India Team Cost */}
          <section className="mt-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              What Goes Into Your India Team Cost?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p className="mb-4">
                When you build an development center in India, your total cost includes more than just salaries. 
                Our India GCC cost calculator factors in:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Base salaries</strong> — vary by role (Full-Stack, Cloud/DevOps, AI/ML) and seniority (Junior, Mid, Senior)</li>
                <li><strong>Benefits & statutory costs</strong> — 28% on top of base salary in India (vs. 25% in the US)</li>
                <li><strong>HR & recruiting</strong> — approximately 10% of annual salary for GCC and Managed models</li>
                <li><strong>GCC setup costs</strong> — entity registration, compliance, and initial infrastructure ($40K–$120K depending on team size)</li>
                <li><strong>Management/partner fees</strong> — ongoing operational support at 40% of base salaries</li>
              </ul>
              <p>
                The India engineer hourly rate you see in results reflects all these costs divided by 2,080 working hours per year — 
                giving you a true apples-to-apples comparison with US engineering costs.
              </p>
            </div>
          </section>

          {/* SEO Content: Three Ways to Engage */}
          <section className="mt-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Three Ways to Engage — and When Each Makes Sense
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Staff Augmentation</h3>
                  <p className="text-gray-600 mb-4">
                    Engineers join your existing team directly. Best for: short-term projects, specific skill gaps, or testing the India market.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>✓ Fastest to start (3-4 weeks)</li>
                    <li>✓ No entity setup required</li>
                    <li>✓ Flexible scaling</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-2 border-red-200 bg-red-50/30">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">GCC (Recommended)</h3>
                  <p className="text-gray-600 mb-4">
                    Your own India entity with dedicated employees. Best for: long-term teams of 10+, full IP ownership, and maximum cost efficiency.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>✓ Full IP control</li>
                    <li>✓ Best 3-year TCO</li>
                    <li>✓ Your company culture</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-2">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Managed Services</h3>
                  <p className="text-gray-600 mb-4">
                    Fully managed delivery team with outcomes-based engagement. Best for: defined projects, less management overhead.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>✓ Turnkey solution</li>
                    <li>✓ Defined deliverables</li>
                    <li>✓ Lower setup cost</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* SEO Content: Why CTOs Choose Niva Digital */}
          <section className="mt-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Why CTOs Choose Niva Digital
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Bengaluru-First Talent Network</h3>
                  <p className="text-gray-600 text-sm">Access to 880+ GCC ecosystem in India's #1 tech hub for AI/ML and product engineering.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">60-90 Day GCC Setup</h3>
                  <p className="text-gray-600 text-sm">Entity registration, compliance, and initial hiring handled end-to-end by experienced partners.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">US-India Time Zone Overlap</h3>
                  <p className="text-gray-600 text-sm">4-5 hours of daily overlap with US Pacific/Mountain time zones for real-time collaboration.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">IP & Compliance Ready</h3>
                  <p className="text-gray-600 text-sm">Strong IP laws, enforceable NDAs, and DPDP Act (2025) alignment with GDPR standards.</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section for SEO */}
          <section className="mt-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">How much does it cost to hire a software engineer in India?</h3>
                  <p className="text-gray-600 text-sm">
                    A mid-level India engineer costs $23,000-$36,000 fully loaded per year (salary + 28% statutory). 
                    A US equivalent costs $120,000-$180,000. Stack matters: Cloud/DevOps and AI/ML command a 15-25% premium in both markets.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">How long does it take to set up an development center in India?</h3>
                  <p className="text-gray-600 text-sm">
                    Staff Augmentation: 3-4 weeks. Full GCC setup including entity registration, compliance, and initial hiring: 60-90 days with an experienced partner.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">What is the difference between a GCC and outsourcing in India?</h3>
                  <p className="text-gray-600 text-sm">
                    A GCC is wholly owned by your company — engineers are your employees, IP is fully yours. 
                    Outsourcing delegates to a third-party vendor. GCCs deliver better IP control, retention, and long-term cost efficiency for teams of 10 or more.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Is India cheaper than Eastern Europe for software development?</h3>
                  <p className="text-gray-600 text-sm">
                    Yes. India rates run $18-$45/hr vs. $25-$70/hr in Eastern Europe (Poland, Romania, Ukraine) per 2025 benchmarks. 
                    Fully loaded annual costs are 30-40% lower for comparable India engineers.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">What is the effective hourly rate for an India GCC engineer?</h3>
                  <p className="text-gray-600 text-sm">
                    All-in effective rate (salary + benefits + recruiting + setup + management, over 2,080 hrs/yr) runs $18-$35/hr by seniority. 
                    A mid-level Full-Stack engineer in Bengaluru works out to $22-$28/hr.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">What are the best cities in India to hire engineers for a GCC?</h3>
                  <p className="text-gray-600 text-sm">
                    Bengaluru (880+ GCCs) leads for AI/ML and product engineering. Hyderabad is fastest-growing. 
                    Pune and Chennai offer 10-15% cost savings. Tier-2 cities like Coimbatore and Indore offer 20-30% savings.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">What is the 3-year TCO for an India engineering team?</h3>
                  <p className="text-gray-600 text-sm">
                    A 10-person GCC team: $1.2M-$1.8M over 3 years vs. $5.5M-$7M for a US equivalent — saving $3.5M-$5M. 
                    A 50-100 person GCC has 40-60% lower annual operating costs than the US equivalent.
                  </p>
                </CardContent>
              </Card>
              <Card className="border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Is it safe to hire engineers in India for a US tech company?</h3>
                  <p className="text-gray-600 text-sm">
                    Yes, with proper structure. India has strong IP laws and enforceable NDAs. The DPDP Act (2025) aligns with GDPR for data handling. 
                    Risk is operational, not legal — clear US-India escalation paths are the critical success factor.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Final CTA */}
          <section className="mt-12 mb-8">
            <Card className="bg-gradient-to-r from-gray-900 to-gray-800 border-0">
              <CardContent className="py-10 text-center">
                <h2 className="text-2xl font-bold text-white mb-3">Ready to See the Full Picture?</h2>
                <p className="text-gray-400 mb-6 max-w-lg mx-auto">
                  This calculator gives you a starting point. Book a strategy call to get a custom roadmap with exact timelines, 
                  role-specific salary benchmarks, and a detailed 3-year TCO analysis for your specific requirements.
                </p>
                <a 
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-amber-400 hover:bg-amber-500 text-gray-900 font-bold px-6 py-5">
                    Book My Free 30-Minute Strategy Call <ArrowRight className="ml-2" size={18} />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </section>
        </div>
      </section>
    </div>
  );
};

export default GCCCalculator;
